/**
 * 練門(✏️)—— 學習端入口整併「練/考合一」(spec ★定案、guardrail #4)。
 *
 * 吃掉舊入口:出練習卷 + 出考卷中心 + 變形卷 + 數學卷 + 聽力卷 + 真題 + 模擬考。
 * **不重寫**任何既有出卷功能,只重組入口 / 改路由(鐵律):
 *   - 主路徑 = `runExamCenter`(出考卷中心:選科目 → 選範圍 → 自動出一張 → 線上作答 /
 *     列印 A4 / 重抽)。這就是 spec 要的「先選科目 → 範圍 → 預設自動出一張 → 馬上逐題對
 *     → 可印 A4」,不一進門就問卷種。
 *   - 「📝 計時模擬段考」開關 = 模擬考(`renderMockExamPicker`,寫完才對 + 看分)。
 *   - 進階「更多卷種」摺疊 ≤5 項:真題練習 / 數學考卷 / 英語聽力卷 / 自選範圍出卷(舊
 *     practice-center)/ 考卷紀錄出變形卷。任一層 ≤5(guardrail #4)。
 *
 * 視覺 = 學術風(pageShell / 門色靛藍強調)。學習/遊戲分離:純學習側,絕不碰
 * commanderXp / 戰鬥數值。
 */

import { pageShell, academicCard } from '../ui/components';
import { DOORS } from './doors';
import { runExamCenter } from '../review/exam-center';
import { runMathExamLaunch } from '../review/math-exam-launch';
import { runListeningExamLaunch } from '../review/listening-exam-launch';
import { runExamPractice } from '../exam-bank/practice';
import { runPracticeCenter } from '../review/bank-paper';
import { renderExamArchive } from '../review/exam-archive-view';

/** 練門依賴的子流程(預設用真實 import,測試可注入 stub)。 */
export interface PracticeDoorDeps {
  runExamCenter: (c: HTMLElement, onDone: () => void) => void;
  runMockExam: (c: HTMLElement, onBack: () => void) => void;
  runExamPractice: (c: HTMLElement, onDone: () => void) => void;
  runMathExam: (c: HTMLElement, onDone: () => void) => void;
  runListeningExam: (c: HTMLElement, onDone: () => void) => void;
  runPracticeCenter: (c: HTMLElement, onDone: () => void) => void;
  runExamArchive: (c: HTMLElement, onDone: () => void) => void;
}

/**
 * 練門入口。`mockExam` 由 main.ts 注入(模擬考 picker 走 onBack 簽名,與其他子流程
 * 不同,故獨立帶入)。其餘子流程預設綁真實 import。
 */
export function runPracticeDoor(
  container: HTMLElement,
  onBack: () => void,
  mockExam: (c: HTMLElement, onBack: () => void) => void,
  deps?: Partial<PracticeDoorDeps>,
): void {
  const d: PracticeDoorDeps = {
    runExamCenter,
    runMockExam: mockExam,
    runExamPractice,
    runMathExam: runMathExamLaunch,
    runListeningExam: runListeningExamLaunch,
    runPracticeCenter,
    runExamArchive: renderExamArchive,
    ...deps,
  };
  renderMenu(container, onBack, d);
}

function renderMenu(container: HTMLElement, onBack: () => void, d: PracticeDoorDeps): void {
  container.innerHTML = '';
  const spec = DOORS.practice;
  const shell = pageShell(container, {
    title: '練',
    subtitle: '選一個科目就自動出一張卷,馬上逐題對,也能印成 A4 用筆寫。',
    onBack,
  });
  shell.root.style.setProperty('--door-accent', spec.accent);

  // 子流程開啟前先取消註冊本頁返回處理器(避免本頁 + 子頁返回疊兩層),回來時重繪本頁
  // 會重新 push。子流程自己負責掛它的返回(統一 ‹ / 滑動)。
  const open = (run: (c: HTMLElement, done: () => void) => void): void => {
    shell.unmount();
    container.innerHTML = '';
    run(container, () => renderMenu(container, onBack, d));
  };

  const root = document.createElement('div');
  root.className = 'practice-door';
  root.setAttribute('data-testid', 'practice-door');
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  // ── 主動作:自動出一張(出考卷中心)。一進門就是這個,不先問卷種。 ──
  const auto = academicCard({
    icon: '📝',
    label: '自動出一張卷',
    sub: '選科目 → 範圍 → 馬上出一張,能線上對也能印',
    onClick: () => open((c, done) => d.runExamCenter(c, done)),
  });
  auto.id = 'practice-auto';
  auto.classList.add('ef-card--accent');
  auto.style.setProperty('--door-accent', spec.accent);
  root.appendChild(auto);

  // ── 計時模擬段考開關(= 模擬考:寫完才對、看分)。 ──
  const timed = academicCard({
    icon: '⏱️',
    label: '計時模擬段考',
    sub: '像真的段考:寫完才對答案、看分數',
    onClick: () => open((c, done) => d.runMockExam(c, done)),
  });
  timed.id = 'practice-timed';
  timed.classList.add('ef-card--accent');
  timed.style.setProperty('--door-accent', spec.accent);
  root.appendChild(timed);

  // ── 更多卷種(摺疊,進階)。任一層 ≤5 項(guardrail #4)。 ──
  const moreWrap = document.createElement('div');
  moreWrap.style.cssText = 'display:flex;flex-direction:column;gap:10px;';
  root.appendChild(moreWrap);

  const moreToggle = document.createElement('button');
  moreToggle.type = 'button';
  moreToggle.id = 'practice-more-toggle';
  moreToggle.className = 'ef-btn ef-btn--secondary';
  moreToggle.style.alignSelf = 'flex-start';
  moreToggle.textContent = '更多卷種 ▼';
  moreWrap.appendChild(moreToggle);

  const moreList = document.createElement('div');
  moreList.id = 'practice-more-list';
  moreList.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  moreWrap.appendChild(moreList);

  const moreSpecs: { id: string; icon: string; label: string; sub: string; run: (c: HTMLElement, done: () => void) => void }[] = [
    { id: 'practice-more-exampractice', icon: '📜', label: '老師的真卷', sub: '練老師輸入的段考真卷', run: (c, done) => d.runExamPractice(c, done) },
    { id: 'practice-more-math', icon: '🧮', label: '數學整張卷', sub: '自動出一整張數學卷,可重抽 / 列印', run: (c, done) => d.runMathExam(c, done) },
    { id: 'practice-more-listening', icon: '🔊', label: '英語聽力卷', sub: '聽發音作答的聽力卷,可重抽 / 列印', run: (c, done) => d.runListeningExam(c, done) },
    { id: 'practice-more-bank', icon: '📚', label: '自選範圍出卷', sub: '從題庫挑範圍抽題,印 A4 用筆寫', run: (c, done) => d.runPracticeCenter(c, done) },
    { id: 'practice-more-variant', icon: '🔁', label: '拍過的卷再變一張', sub: '拍過的考卷換數字 / 情境再練一次', run: (c, done) => d.runExamArchive(c, done) },
  ];

  for (const m of moreSpecs) {
    const card = academicCard({ icon: m.icon, label: m.label, sub: m.sub, onClick: () => open(m.run) });
    card.id = m.id;
    moreList.appendChild(card);
  }

  moreToggle.addEventListener('click', () => {
    const open = moreList.style.display !== 'none';
    moreList.style.display = open ? 'none' : 'flex';
    moreToggle.textContent = open ? '更多卷種 ▼' : '收起 ▲';
  });
}
